/**
 * THE SPECIFICATION SHEET, BUILT FROM WHAT THE SHOP ACTUALLY HOLDS.
 *
 * The merchant's product page carries a tab of these and ours carried none,
 * even though every fact was already in our database: the SKU a shopper checks
 * against the box and the warranty card, the brand on a shop called "House of
 * Brands", the row they picked, and the weight and box size where the shop has
 * measured them.
 *
 * One rule, and it is the only thing that keeps a panel like this honest: a
 * row exists only when it has a value. An empty "Specifications" tab is worse
 * than no tab at all, and a dimension printed from a half-filled box —
 * "12 × — × 6" — is worse than both.
 */

/** A value worth printing: a non-blank string or a positive number. */
const present = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'number') return Number.isFinite(v) && v > 0;
  return String(v).trim() !== '';
};

const text = (v) => String(v).trim();

function formatWeight(grams, t) {
  if (!present(grams)) return null;
  const g = Number(grams);
  if (g < 1000) return t('productDetail.specWeightGrams', { defaultValue: '{{value}} g', value: String(g) });
  const kg = g / 1000;
  const shown = Number.isInteger(kg) ? String(kg) : kg.toFixed(1);
  return t('productDetail.specWeightKilos', { defaultValue: '{{value}} kg', value: shown });
}

function formatDimensions(box, t) {
  if (!box || typeof box !== 'object') return null;
  const { lengthCm, widthCm, heightCm } = box;
  // A box is three numbers or it is not a box. Two of them and a gap is a
  // measurement nobody took, printed as if somebody had.
  if (![lengthCm, widthCm, heightCm].every(present)) return null;
  return t('productDetail.specDimensions', {
    defaultValue: '{{length}} × {{width}} × {{height}} cm',
    length: String(Number(lengthCm)),
    width: String(Number(widthCm)),
    height: String(Number(heightCm)),
  });
}

/** The three legacy axis slots, with the labels the shop gave them. */
const AXES = [
  ['color', 'productDetail.specColour', 'Colour'],
  ['size', 'productDetail.specSize', 'Size'],
  ['style', 'productDetail.specType', 'Type'],
];

/**
 * `{ product, variant, t }` → `[{ key, label, value }]`, in the order a
 * shopper reads them. Empty when the shop holds nothing worth a panel, which
 * is the signal the page uses to leave the tab out entirely.
 */
export function buildProductSpecs({ product, variant, t, valueLabel, shownSlot }) {
  const rows = [];
  const p = product || {};
  const v = variant || {};
  const say = (key, fallback) => t(key, { defaultValue: fallback });

  if (present(v.sku)) {
    rows.push({ key: 'sku', label: say('productDetail.specSku', 'SKU'), value: text(v.sku) });
  }
  if (present(p.brand && p.brand.name)) {
    rows.push({ key: 'brand', label: say('productDetail.specBrand', 'Brand'), value: text(p.brand.name) });
  }
  if (present(p.category && p.category.name)) {
    rows.push({ key: 'category', label: say('productDetail.specCategory', 'Category'), value: text(p.category.name) });
  }
  for (const [slot, key, fallback] of AXES) {
    if (!present(v[slot])) continue;
    /* A value the shopper has not chosen yet is not a fact of what they will get (run 4, round B: S3): the page shows
     * its first variant for a photo and a price, and the sheet printed «نكهة: كيندر» before any flavour was tapped. */
    if (typeof shownSlot === 'function' && !shownSlot(slot)) continue;
    const label = (p.axisLabels && present(p.axisLabels[slot])) ? text(p.axisLabels[slot]) : say(key, fallback);
    // The value in the reader's language when the page hands its resolver over (utils/shopperText).
    rows.push({ key: slot, label, value: text(typeof valueLabel === 'function' ? valueLabel(slot, v[slot]) : v[slot]) });
  }
  // The row's own measurement first: a shop that weighs each size has said
  // something more precise than the product default, and it should win.
  const weight = formatWeight(present(v.packageWeightGrams) ? v.packageWeightGrams : p.packageWeightGrams, t);
  if (weight) rows.push({ key: 'weight', label: say('productDetail.specWeight', 'Weight'), value: weight });
  const box = formatDimensions(v.boxDimensions || p.boxDimensions, t);
  if (box) rows.push({ key: 'dimensions', label: say('productDetail.specDimensionsLabel', 'Dimensions'), value: box });

  return rows;
}
